<template>
  <depreciation-panels
    :canDelete="canDelete"
    :items="value.depreciacoes"
    :periodoDepreciacao="value.periodo_depreciacao"
    :projectsByYear="projectsByYear"
    :valid.sync="valid.depreciacoes"
    @input="inputHandler('depreciacoes', $event)"
    @loadProjectsFromYear="$emit('loadProjectsFromYear', $event)"
  >
    <input-v
      v-for="(field, key) in fields"
      class="pl-4"
      :class="{ readonly: field.readonly }"
      :colSize="field.colSize"
      :editable="field.editable"
      :key="key"
      :label="field"
      :opts="opts"
      :valid.sync="valid[field.key]"
      :value="value[field.key]"
      @input="inputHandler(field.key, $event)"
    ></input-v>
    <v-col v-if="mensagemValorDeprecAbaixoDoCentavo" :cols="12" class="py-0 pl-4">
      <v-alert
        data-testid="alerta-valor-deprec-abaixo-centavo"
        dense
        style="font-size: 13px"
        text
        type="warning"
      >
        {{ mensagemValorDeprecAbaixoDoCentavo }}
      </v-alert>
    </v-col>
  </depreciation-panels>
</template>

<script>
// Teto do período de utilização, em meses (10 anos). Espelha
// NFDepreciacaoController::PERIODO_DEPRECIACAO_MAXIMO no backend.
const PERIODO_DEPRECIACAO_MAXIMO = 120;

// Menor valor depreciado mensal representável em reais. A coluna valor_deprec é
// decimal(20,2): abaixo de um centavo o item seria gravado com depreciação 0,00.
const VALOR_DEPREC_MINIMO = 0.01;

// Arredonda como a coluna decimal(20,2) faz, evitando o ruído de ponto flutuante
// (0,29 / 29 dá 0,00999... em JS, mas o banco grava 0,01).
const arredondaCentavos = (valor) => Math.round(valor * 100) / 100;

// O valor do item em centavos é exatamente quantos meses cabem antes de o rateio zerar
// (item de R$ 1,00 → 100 meses). O teto de 120 continua valendo por cima disso.
const periodoMaximoDoItem = (valorItem) => Math.round(valorItem * 100);

// Texto único do aviso de arredondamento: usado no toast e no alerta abaixo dos campos.
const mensagemAbaixoDoCentavo = (valorItem) =>
  'O "Valor Depreciado" deste item ficaria menor que R$ 0,01 e seria gravado como R$ 0,00. ' +
  `Para este valor de item, o período máximo é de ${periodoMaximoDoItem(valorItem)} meses.`;

export default {
  components: {
    "depreciation-panels": () => import("./depreciation.panels.vue"),
    "input-v": () => import("@/components/input-v.vue"),
  },
  computed: {
    fields: function () {
      const all = [
        {
          key: "descricao",
          name: "Descrição do Item",
          type: this.$fieldTypes.TEXT,
          colSize: 6,
          rules: [{ rule: "required" }],
        },
        {
          key: "finalidade",
          name: "Classificação LI",
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "finalidades", key: "value", name: "value" },
          colSize: 6,
          rules: [{ rule: "required" }],
        },
        {
          key: "valor",
          name: "Valor do Item",
          type: this.$fieldTypes.MONEY,
          colSize: 3,
          rules: [{ rule: "required" }],
        },
        {
          key: "periodo_depreciacao",
          name: "Período de Utilização (em meses)",
          type: this.$fieldTypes.NUMBER,
          colSize: 3,
          rules: [
            { rule: "required" },
            { rule: "min", params: { size: 1 } },
            { rule: "max", params: { size: PERIODO_DEPRECIACAO_MAXIMO } },
            this.validaValorDeprecMinimo,
          ],
        },
        {
          key: "valor_deprec",
          name: "Valor Depreciado",
          type: this.$fieldTypes.MONEY,
          editable: false,
          readonly: true,
          colSize: 3,
          hide: !this.showValorDeprec,
        },
      ];

      return all.filter(({ hide }) => !hide);
    },
  },
  created: function () {
    this.$emit('update:valid', this.generateUpdateValid());
  },
  data: function () {
    return {
      // Evita repetir o aviso do valor abaixo de um centavo a cada tecla digitada:
      // ele só volta a ser exibido depois que o item sai da situação.
      avisouValorDeprecAbaixoDoCentavo: false,
      // Alerta fixo abaixo dos campos: diferente do toast, fica visível enquanto o item
      // estiver na situação.
      mensagemValorDeprecAbaixoDoCentavo: null,
      opts: {
        finalidades: [
          { value: "EQUIPAMENTOS" },
          { value: "SOFTWARE (COMPRA)" },
          { value: "SOFTWARE (LICENÇA DE USO)" },
        ],
      },
      showValorDeprec: true,
      timeoutId: null,
      valid: {
        depreciacoes: null,
        descricao: null,
        finalidade: null,
        periodo_depreciacao: null,
        valor: null,
        valor_deprec: null,
      },
    };
  },
  methods: {
    avisaValorDeprecAbaixoDoCentavo: function (abaixoDoCentavo, valorItem) {
      if (!abaixoDoCentavo) {
        this.avisouValorDeprecAbaixoDoCentavo = false;
        this.mensagemValorDeprecAbaixoDoCentavo = null;
        return;
      }

      const mensagem = mensagemAbaixoDoCentavo(valorItem);
      this.mensagemValorDeprecAbaixoDoCentavo = mensagem;

      // Os watchers de valor e período disparam a cada tecla: sem essa trava o mesmo
      // aviso empilharia um toast por dígito digitado.
      if (this.avisouValorDeprecAbaixoDoCentavo) {
        return;
      }

      this.avisouValorDeprecAbaixoDoCentavo = true;
      this.$notify({
        title: "Atenção",
        group: "geral",
        // vue-notification só renderiza `text` — com `message` o toast saía só com o título
        text: mensagem,
        type: "warn",
        duration: 5000,
      });
    },
    generateUpdateValid: function () {
      const self = this;
      return () => {
        const depreciacoesFn = self.valid.depreciacoes;
        const numDepreciacoesErradas = typeof depreciacoesFn === 'function' ? depreciacoesFn() : 0;
        const campos = Object
          .entries(self.valid)
          .filter(([key]) => key !== 'depreciacoes')
          .reduce((acc, [key, fn]) => {
            if (fn && fn()) {
              return acc;
            }

            const field = self.fields.find((field) => field.key === key);
            return field ? [ ...acc, field.name ] : acc;
          }, []);
        return numDepreciacoesErradas === 0 ? campos : campos.concat([`${numDepreciacoesErradas} depreciações erradas`])
      }
    },
    inputHandler: function (key, value) {
      this.$emit('input', {
        ...this.value,
        [key]: value,
      });
    },
    validaValorDeprecMinimo: function (periodoDepreciacaoInformado) {
      const periodoDepreciacao = parseFloat(periodoDepreciacaoInformado);
      const valorItem = parseFloat(this.value.valor);

      if (!(periodoDepreciacao >= 1) || !(valorItem > 0)) {
        // Sem os dois valores não há o que checar: as demais regras já cobrem o caso
        return true;
      }

      if (arredondaCentavos(valorItem / periodoDepreciacao) >= VALOR_DEPREC_MINIMO) {
        return true;
      }

      return `O valor depreciado ficaria menor que R$ 0,01. Para este valor de item, o período máximo é de ${periodoMaximoDoItem(valorItem)} meses.`;
    },
    updateValorDeprec: function () {
      const { periodo_depreciacao, valor } = this.value;
      const periodoDepreciacao = parseFloat(periodo_depreciacao);
      const valorItem = parseFloat(valor);
      // O período mínimo é de 1 mês: com 1 mês o valor depreciado é o próprio valor do item.
      // Acima do teto o resultado não é representável em reais — não faz sentido exibir nada.
      const temValores =
        periodoDepreciacao >= 1 &&
        periodoDepreciacao <= PERIODO_DEPRECIACAO_MAXIMO &&
        valorItem > 0;
      // Mesma comparação da regra do campo (`validaValorDeprecMinimo`): o que decide é o
      // valor **arredondado**, que é o que a coluna decimal(20,2) vai gravar.
      const abaixoDoCentavo =
        temValores &&
        arredondaCentavos(valorItem / periodoDepreciacao) < VALOR_DEPREC_MINIMO;

      this.avisaValorDeprecAbaixoDoCentavo(abaixoDoCentavo, valorItem);

      const podeCalcular = temValores && !abaixoDoCentavo;
      this.value.valor_deprec = podeCalcular ? valorItem / periodoDepreciacao : 0;
      // Como o input-v não atualiza programaticamente após ser renderizado, o truque é forçar ele a re-renderizar de novo
      clearTimeout(this.timeoutId);
      this.showValorDeprec = false;
      this.timeoutId = setTimeout(() => this.showValorDeprec = true, 50);      
    },
  },
  props: {
    canDelete: {
      type: Boolean,
      default: false,
    },
    projectsByYear: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Object,
      default: () => ({
        depreciacoes: [],
        descricao: null,
        periodo_depreciacao: null,
        valor: null,
        valor_deprec: null,
      }),
    }
  },
  watch: {
    'value.periodo_depreciacao': function () {
      this.updateValorDeprec();
    },
    'value.valor': function () {
      this.updateValorDeprec();
    },
  },
}
</script>

<style lang="scss" scoped>
.readonly ::v-deep .v-input__slot {
  background-color: #e9ecef;
  border-color: #6c757d;

  &, & input {
    cursor: default;
  }
}
</style>
